import { useState, FormEvent } from 'react';
import { Link, useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AuthCard, PasswordInput } from '@/components/auth';
import { useAuthFlow } from '@/hooks/useAuthFlow';
import { usePasswordValidation, isValidEmail } from '@/hooks/usePasswordValidation';
import { useAuth } from '@/hooks/useAuth';
import { Loader2, AlertCircle, Sparkles } from 'lucide-react';

export default function Signup() {
  const [, setLocation] = useLocation();
  const { mockLogin } = useAuth();
  const { signup, isSubmitting, error, clearError, setPendingEmail } = useAuthFlow();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState<{
    email?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  const passwordValidation = usePasswordValidation(password);

  const validateForm = (): boolean => {
    const errors: typeof validationErrors = {};

    if (!email) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (!passwordValidation.isValid) {
      errors.password = 'Password does not meet requirements';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();

    if (!validateForm()) {
      return;
    }

    const result = await signup(email, password);

    if (result.success) {
      // Navigate to verification page
      setPendingEmail(email);
      setLocation('/verify-email');
    }
  };

  const handleDemoLogin = () => {
    mockLogin();
    setLocation('/');
  };

  return (
    <AuthCard
      title="Create your account"
      description="Start predicting beautiful sunrises and sunsets"
      footer={
        <p>
          Already have an account?{' '}
          <Link href="/login" className="text-amber-600 hover:text-amber-700 font-medium">
            Sign in
          </Link>
        </p>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (validationErrors.email) {
                setValidationErrors(prev => ({ ...prev, email: undefined }));
              }
            }}
            placeholder="you@example.com"
            autoComplete="email"
            disabled={isSubmitting}
            className={validationErrors.email ? 'border-red-500' : ''}
          />
          {validationErrors.email && (
            <p className="text-sm text-red-500" role="alert" aria-live="polite">{validationErrors.email}</p>
          )}
        </div>

        <PasswordInput
          id="password"
          label="Password"
          value={password}
          onChange={(value) => {
            setPassword(value);
            if (validationErrors.password) {
              setValidationErrors(prev => ({ ...prev, password: undefined }));
            }
          }}
          placeholder="Create a strong password"
          showStrength={true}
          error={validationErrors.password}
          disabled={isSubmitting}
        />

        <PasswordInput
          id="confirmPassword"
          label="Confirm Password"
          value={confirmPassword}
          onChange={(value) => {
            setConfirmPassword(value);
            if (validationErrors.confirmPassword) {
              setValidationErrors(prev => ({ ...prev, confirmPassword: undefined }));
            }
          }}
          placeholder="Confirm your password"
          error={validationErrors.confirmPassword}
          disabled={isSubmitting}
          autoComplete="new-password"
        />

        <Button
          type="submit"
          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating account...
            </>
          ) : (
            'Create Account'
          )}
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">Or</span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={handleDemoLogin}
          disabled={isSubmitting}
        >
          <Sparkles className="mr-2 h-4 w-4" />
          Try Demo Mode
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          By creating an account, you agree to our{' '}
          <Link href="/terms" className="text-amber-600 hover:underline">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link href="/privacy" className="text-amber-600 hover:underline">
            Privacy Policy
          </Link>
        </p>
      </form>
    </AuthCard>
  );
}
